import { useEffect, useState } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { Banknote, CalendarDays, FileSignature, Gift, Timer } from 'lucide-react';

import { useAuth } from '../../core/auth/AuthProvider';
import { db } from '../../core/firebase/firebase';

function displayDate(value) {
  if (!value) return 'Non renseignée';
  const match = String(value).match(/^(\d{4})-(\d{2})-(\d{2})$/);
  return match ? `${match[3]}/${match[2]}/${match[1]}` : String(value);
}

function money(value) {
  if (value === null || value === undefined || value === '') return 'Non renseigné';
  const amount = Number(value);
  if (!Number.isFinite(amount)) return 'Non renseigné';
  return new Intl.NumberFormat('fr-TN', { minimumFractionDigits: 0, maximumFractionDigits: 3 }).format(amount) + ' DT';
}

export default function TeacherContractPage() {
  const { user } = useAuth();
  const [contract, setContract] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!user?.uid) return undefined;
    setLoading(true);
    setError('');
    return onSnapshot(doc(db, 'teacherContracts', user.uid), (snapshot) => {
      setContract(snapshot.exists() ? { id: snapshot.id, ...snapshot.data() } : null);
      setLoading(false);
    }, (snapshotError) => {
      console.error('Contrat enseignant :', snapshotError);
      setError('Impossible de charger votre contrat pour le moment.');
      setLoading(false);
    });
  }, [user?.uid]);

  if (loading) return <div className="parent-empty large">Chargement de votre contrat…</div>;

  if (error) return <div className="alert alert-error">{error}</div>;

  if (!contract) {
    return (
      <section>
        <div className="parent-section-title">
          <p className="eyebrow">DOSSIER PROFESSIONNEL</p>
          <h2>Mon contrat</h2>
          <p>Consultez les informations contractuelles enregistrées par la gérance.</p>
        </div>
        <div className="complaint-empty-v3 teacher-contract-empty-v1">
          <FileSignature size={30} />
          <strong>Aucun contrat enregistré pour le moment</strong>
          <span>Votre contrat apparaîtra ici dès qu’il aura été créé par la gérance.</span>
        </div>
      </section>
    );
  }

  const bonuses = (Array.isArray(contract.bonuses) ? contract.bonuses : []).filter((bonus) => bonus?.amount !== null && bonus?.amount !== undefined && bonus?.amount !== '' || bonus?.paymentDate);

  return (
    <section className="teacher-contract-page-v1">
      <div className="parent-section-title">
        <p className="eyebrow">DOSSIER PROFESSIONNEL</p>
        <h2>Mon contrat</h2>
        <p>Retrouvez votre CDI, votre rémunération et les primes enregistrées par la gérance.</p>
      </div>

      <article className="teacher-contract-hero-v1">
        <div className="teacher-contract-hero-icon-v1"><FileSignature size={28} /></div>
        <div>
          <span>TYPE DE CONTRAT</span>
          <strong>CDI — Contrat à durée indéterminée</strong>
          <small>En vigueur à compter du {displayDate(contract.effectiveDate || contract.startDate)}</small>
        </div>
      </article>

      <div className="teacher-contract-grid-v1">
        <article className="parent-card-v0 teacher-contract-card-v1">
          <div className="parent-card-v0-heading"><div><p className="eyebrow">CONTRAT</p><h3>Informations principales</h3></div><CalendarDays size={20} /></div>
          <dl className="parent-details">
            <div><dt>Date de début</dt><dd>{displayDate(contract.startDate)}</dd></div>
            <div><dt>Date d’effet</dt><dd>{displayDate(contract.effectiveDate)}</dd></div>
            <div><dt>Fonction</dt><dd>{contract.position || 'Enseignant(e)'}</dd></div>
            <div><dt>Temps de travail</dt><dd>{contract.weeklyHours ? `${contract.weeklyHours} h / semaine` : 'Non renseigné'}</dd></div>
          </dl>
        </article>

        <article className="parent-card-v0 teacher-contract-card-v1">
          <div className="parent-card-v0-heading"><div><p className="eyebrow">RÉMUNÉRATION</p><h3>Salaire</h3></div><Banknote size={20} /></div>
          <div className="teacher-contract-salary-v1">
            <span>Salaire de base</span>
            <strong>{money(contract.baseSalary)}</strong>
          </div>
          {contract.observations && <div className="teacher-contract-note-v1"><span>Observations</span><p>{contract.observations}</p></div>}
        </article>
      </div>

      <article className="parent-card-v0 teacher-bonuses-card-v1">
        <div className="parent-card-v0-heading"><div><p className="eyebrow">PRIMES</p><h3>Primes trimestrielles</h3></div><Gift size={20} /></div>
        <div className="teacher-bonus-list-v1">
          {bonuses.map((bonus, index) => (
            <div className="teacher-bonus-row-v1" key={`${bonus.term || 'prime'}-${index}`}>
              <span className="teacher-bonus-icon-v1"><Timer size={17} /></span>
              <div><strong>{bonus.term || `Prime ${index + 1}`}</strong><small>Date de paiement : {displayDate(bonus.paymentDate)}</small></div>
              <b>{money(bonus.amount)}</b>
            </div>
          ))}
          {!bonuses.length && <div className="parent-empty">Aucune prime enregistrée.</div>}
        </div>
      </article>
    </section>
  );
}
